import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Link,
  Chip,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import TouchAppIcon from '@mui/icons-material/TouchApp';

export const AdminTrack = () => {
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(100);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [rssSearch, setRssSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ days, limit });
      if (nameSearch.trim()) params.set('q', nameSearch.trim());
      if (rssSearch.trim()) params.set('rssUrl', rssSearch.trim());
      const r = await fetch(`/api/top-podcasts/bi/track/shows?${params}`);
      const json = await r.json();
      if (!json.ok) throw new Error(json.error || 'Request failed');
      setRows(json.rows || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = rows.reduce(
    (acc, r) => ({
      users: acc.users + r.userCount,
      selections: acc.selections + r.selections,
    }),
    { users: 0, selections: 0 }
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ color: '#1976d2' }}>
          Tracking
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Podcasts tracked in Analyze — unique users and activity breakdown
        </Typography>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select value={days} label="Period" onChange={(e) => setDays(e.target.value)}>
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                  <MenuItem value={365}>Last 365 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Limit</InputLabel>
                <Select value={limit} label="Limit" onChange={(e) => setLimit(e.target.value)}>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by podcast name…"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Filter by RSS URL…"
                value={rssSearch}
                onChange={(e) => setRssSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={load}
                disabled={loading}
                fullWidth
                sx={{ height: 40 }}
              >
                {loading ? <CircularProgress size={22} /> : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      {rows.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Shows Tracked', value: rows.length, icon: null },
            { label: 'Unique Users', value: totals.users, icon: <PeopleIcon fontSize="small" /> },
            { label: 'Podcast Selections', value: totals.selections, icon: <TouchAppIcon fontSize="small" /> },
          ].map(({ label, value, icon }) => (
            <Grid item key={label}>
              <Paper variant="outlined" sx={{ px: 3, py: 1.5, minWidth: 140 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mb: 0.5 }}>
                  {icon}
                  <Typography variant="caption">{label}</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Table */}
      {loading && !rows.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ width: 44, pl: 2 }}><strong>#</strong></TableCell>
                    <TableCell><strong>Podcast</strong></TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <PeopleIcon fontSize="small" />
                        <strong>Users</strong>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <TouchAppIcon fontSize="small" />
                        <strong>Selections</strong>
                      </Box>
                    </TableCell>
                    <TableCell><strong>Last Seen</strong></TableCell>
                    <TableCell><strong>RSS</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={row.rssUrl || idx} hover>
                      <TableCell sx={{ pl: 2, color: 'text.secondary', fontSize: 12 }}>{idx + 1}</TableCell>
                      <TableCell sx={{ maxWidth: 280, fontWeight: 500 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.showTitle || row.rssUrl || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.userCount}
                          size="small"
                          color={row.userCount >= 5 ? 'primary' : row.userCount >= 2 ? 'info' : 'default'}
                          variant={row.userCount >= 2 ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700, minWidth: 36 }}
                        />
                      </TableCell>
                      <TableCell align="center">{row.selections}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                        {new Date(row.lastSeenAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {row.rssUrl
                          ? <Link href={row.rssUrl} target="_blank" rel="noopener" sx={{ fontSize: 12 }}>RSS</Link>
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No tracking data for the selected period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};
